import React, { useCallback, useMemo, memo } from 'react'
import useRowHydration from '../../../../../services/community/identity/library/useRowHydration'
import PlaylistShareButton from '../../../shared/PlaylistShareButton/index'
import PlaylistSeeEntireLink from '../../Library/PlaylistSeeEntireLink/index'
import PlaylistSharingDropdown, { VISIBILITY, isPlaylistVisibilityLevel }
  from '../../Library/PlaylistTabs/MyPlaylistsTab/PlaylistSharingDropdown/index'
import type { EditToolbarLabels } from '../CommunityProfileCarousels/index'
import type {
  PlaylistShell, PlaylistVisibilityLevel, EnrichedPlaylistItem,
} from '../../../../../services/community/types'
import RowEditDoneButton from './RowEditDoneButton'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import PlaylistRow from './PlaylistRow'

/**
 * Variant: custom user playlist in edit mode.
 *
 * Edit mode owns the rename input (threaded through to the header)
 * and replaces the overflow menu with a `Done` button in the
 * `nameAction` slot. The body renders `editMode={true}` so
 * `ResponsivePlaylistDisplay` paints the remove + reorder pills on
 * each tile (the CLAUDE.md Community carve-out for raw `<button>`).
 */

export interface EditableMyPlaylistRowProps {
  playlist: PlaylistShell
  language: string
  jwt?: string
  rowAuthorDisplay?: string | null
  allowedVisibilities?: readonly PlaylistVisibilityLevel[]
  editingName: string
  onNameChange: (value: string) => void
  onDoneEditing: (id: string, type?: string, name?: string) => void
  onRemoveItem: (
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
  ) => void
  onReorderItem?: (
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
    newPosition: number,
  ) => void
  onUpdateVisibility?: (id: string, vis: PlaylistVisibilityLevel) => void
  seeMoreLabel: string
  showLessLabel?: string
  seeEntireTemplate: string
  doneLabel: string
  editToolbarLabels?: EditToolbarLabels
}

function EditableMyPlaylistRowImpl ({
  playlist,
  language,
  jwt,
  rowAuthorDisplay = null,
  allowedVisibilities,
  editingName,
  onNameChange,
  onDoneEditing,
  onRemoveItem,
  onReorderItem,
  onUpdateVisibility,
  seeMoreLabel,
  showLessLabel,
  seeEntireTemplate,
  doneLabel,
  editToolbarLabels,
}: EditableMyPlaylistRowProps) {
  const { details, isHydrating } = useRowHydration(playlist, language, { jwt })
  const expectedItemCount = playlist.itemCount ?? 0
  const visibility: PlaylistVisibilityLevel = isPlaylistVisibilityLevel(playlist.visibility)
    ? playlist.visibility
    : VISIBILITY.PRIVATE

  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId: playlist.id,
    playlistName: playlist.playlistName || '',
    playlistType: playlist.playlistType || undefined,
    items: details.items,
    expectedItemCount,
    totalDuration: details.totalDuration ?? null,
    primaryTopic: details.primaryTopic,
    isHydrating,
    isEditing: true,
  }), [
    playlist.id, playlist.playlistName, playlist.playlistType,
    details.items, details.totalDuration, details.primaryTopic,
    expectedItemCount, isHydrating,
  ])

  const handleChangeVisibility = useCallback((vis: PlaylistVisibilityLevel) => {
    if (onUpdateVisibility) onUpdateVisibility(playlist.id, vis)
  }, [onUpdateVisibility, playlist.id])

  const handleDoneClick = useCallback(() => {
    onDoneEditing(playlist.id, playlist.playlistType, playlist.playlistName)
  }, [onDoneEditing, playlist.id, playlist.playlistType, playlist.playlistName])

  // Move Earlier / Move Later — translate (fromIndex, toIndex) coming out of
  // <PlaylistReorderControls> into the (item, newPosition) shape the upstream
  // handler expects. Look up destination's playlistPosition from hydrated items
  // — the server uses that as "place the moved item in this slot".
  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (!onReorderItem) return
    const items = details.items as EnrichedPlaylistItem[]
    const moving = items[fromIndex]
    const dest = items[toIndex]
    if (!moving || !dest) return
    const newPosition = dest.playlistPosition
    if (typeof newPosition !== 'number') return
    onReorderItem(
      playlist.id,
      playlist.playlistType || 'custom',
      moving,
      newPosition,
    )
  }, [onReorderItem, details.items, playlist.id, playlist.playlistType])

  const videoCount = expectedItemCount || details.items.length
  const seeEntireLabel = seeEntireTemplate.replace('{count}', String(videoCount))

  // Stable Body.onRemoveItem — capture `playlist.id` / `playlist.playlistType`
  // from props (matches `handleReorder` above) so the parent can pass a
  // single memoized `(playlistId, playlistType, item)` callback without an
  // inline arrow defeating this component's `React.memo`.
  const handleRemove = useCallback((item: unknown) => {
    onRemoveItem(
      playlist.id,
      playlist.playlistType || 'custom',
      item as EnrichedPlaylistItem,
    )
  }, [onRemoveItem, playlist.id, playlist.playlistType])

  // Memoise the three header slots — see `MyPlaylistRow.tsx` for the
  // canonical rationale. `CommunityPlaylistRowHeader` is `React.memo`'d,
  // and these ReactElement props were re-minted every render, defeating
  // the downstream memo and cascading into `PlaylistShareButton` etc.
  const headerStatusTag = useMemo(() => (
    <PlaylistSharingDropdown
      visibility={visibility}
      onChangeVisibility={handleChangeVisibility}
      allowedVisibilities={
        allowedVisibilities as readonly PlaylistVisibilityLevel[] | undefined
      }
      followerCount={playlist.followerCount || 0}
    />
  ), [visibility, handleChangeVisibility, allowedVisibilities, playlist.followerCount])

  const headerShareButton = useMemo(() => (
    <PlaylistShareButton
      playlistId={playlist.id}
      playlistName={playlist.playlistName || ''}
      description={playlist.description}
      items={details.items as EnrichedPlaylistItem[]}
    />
  ), [playlist.id, playlist.playlistName, playlist.description, details.items])

  const headerNameAction = useMemo(() => (
    <RowEditDoneButton onClick={handleDoneClick} label={doneLabel} />
  ), [handleDoneClick, doneLabel])

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame className="profile-my-playlists__playlist-row">
        <PlaylistRow.Header
          authorDisplay={rowAuthorDisplay}
          followerCount={playlist.followerCount || 0}
          editingName={editingName}
          onNameChange={onNameChange}
          statusTag={headerStatusTag}
          shareButton={headerShareButton}
          nameAction={headerNameAction}
        />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
          hideToggle
          editToolbarLabels={editToolbarLabels}
          onRemoveItem={handleRemove}
          onReorderItem={onReorderItem ? handleReorder : undefined}
        />
        {/*
          Footer slot — three states (CLS reservation):
          real link when items resolve / invisible placeholder during
          hydration with a non-empty shell / nothing when truly empty.
          See `MyPlaylistRow` for the rationale.
        */}
        {(() => {
          if (details.items.length > 0) {
            return (
              <PlaylistSeeEntireLink
                to={`/playlist/${playlist.id}`}
                videoCount={videoCount}
                label={seeEntireLabel}
              />
            )
          }
          if (isHydrating && expectedItemCount > 0) {
            return (
              <PlaylistSeeEntireLink
                to=""
                videoCount={videoCount}
                label={seeEntireLabel}
                placeholder
              />
            )
          }
          return null
        })()}
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const EditableMyPlaylistRow = memo(EditableMyPlaylistRowImpl)
EditableMyPlaylistRow.displayName = 'EditableMyPlaylistRow'
export default EditableMyPlaylistRow
